import { useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useLocation, useNavigate } from "react-router-dom";

import {
  pathOf,
  rememberSession,
  renewRetryDelay,
  requiresSignin,
  type ReturnState,
} from "./session";

/**
 * Keeps the session of a page left open (a war-room screen) alive.
 *
 * - A background renewal that fails (network, provider) is retried with a
 *   growing delay while the access token is still valid.
 * - Temporary failures keep retrying, including after token expiry.
 * - A provider refusal after expiry requires fresh sign-in.
 */
export function SessionKeeper() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const restoring = location.pathname === "/login" || location.pathname === "/postlogin";
  const currentPath = useRef(pathOf(location));
  currentPath.current = pathOf(location);
  const [awaitingRenewalError, setAwaitingRenewalError] = useState(false);
  const retryRenewalRef = useRef<((error?: unknown) => void) | undefined>(undefined);

  const { events, signinSilent, isAuthenticated } = auth;

  // Browsers signed in before sessions were remembered are remembered from now on.
  useEffect(() => {
    if (isAuthenticated && !auth.user?.expired) rememberSession();
  }, [isAuthenticated, auth.user?.expired]);

  useEffect(() => {
    if (restoring) return;
    let attempt = 0;
    let timer: number | undefined;
    let expired = false;
    let renewing = false;
    let active = true;

    function renewed() {
      rememberSession();
      attempt = 0;
      expired = false;
      window.clearTimeout(timer);
      timer = undefined;
    }

    async function renew() {
      if (!active || renewing) return;
      renewing = true;
      try {
        const user = await signinSilent();
        if (active) {
          if (user && !user.expired) renewed();
          else setAwaitingRenewalError(true);
        }
      } catch (error) {
        if (active) retryRenewal(error);
      } finally {
        renewing = false;
      }
    }

    function retryRenewal(error?: unknown) {
      if (!active) return;
      window.clearTimeout(timer);
      if (expired && requiresSignin(error)) {
        active = false;
        const state: ReturnState = { returnTo: currentPath.current, expired: true };
        navigate("/login", { replace: true, state });
        return;
      }
      timer = window.setTimeout(() => {
        attempt += 1;
        void renew();
      }, renewRetryDelay(attempt));
    }
    retryRenewalRef.current = retryRenewal;

    const removers = [
      events.addSilentRenewError(retryRenewal),
      events.addUserLoaded(renewed),
      events.addAccessTokenExpired(() => {
        if (expired) return;
        expired = true;
        window.clearTimeout(timer);
        attempt = 0;
        void renew();
      }),
    ];
    function onOnline() {
      if (!active || (!expired && timer === undefined)) return;
      window.clearTimeout(timer);
      void renew();
    }
    window.addEventListener("online", onOnline);
    return () => {
      active = false;
      retryRenewalRef.current = undefined;
      window.clearTimeout(timer);
      window.removeEventListener("online", onOnline);
      for (const remove of removers) remove();
    };
  }, [events, signinSilent, navigate, restoring]);

  useEffect(() => {
    if (!awaitingRenewalError || auth.isLoading || auth.activeNavigator) return;
    setAwaitingRenewalError(false);
    retryRenewalRef.current?.(auth.error);
  }, [awaitingRenewalError, auth.isLoading, auth.activeNavigator, auth.error]);

  return null;
}
